const STRICT_PROMPTS = {
    inverters: ['pv-inverter-model-details', 'inverter-inverter-label-and-serial-number'],
    modules: ['pv-module-model-details', 'pv-module-serial'],
    batteries: [
        'battery-system-battery-system-label-and-serial-number',
        'battery-system-battery-modules-labels-and-serial-numbers',
    ],
    acIsolators: [
        'pv-ac-isolator-rating',
        'pv-main-switch-inverter-supply',
        'isolators-ac-isolators-before-during-after',
    ],
    dcIsolators: [
        'pv-dc-isolator-before',
        'pv-dc-isolator-during',
        'pv-dc-isolator-after',
        'pv-inverter-integrated-isolator',
        'isolators-dc-load-break-disconnectors',
    ],
};
const KIND_LABEL = {
    inverters: 'Inverter',
    modules: 'Panel',
    batteries: 'Battery',
    acIsolators: 'AC isolator',
    dcIsolators: 'DC isolator',
};
/**
 * Battery hardware helpers for the dashboard.
 *
 * Photo linking is wider than the strict label-prompt pair: a dashboard upload
 * is often filed under a nearby prompt (location, sign, or an inverter label
 * when the job has no inverter row). Suggestions that fill serials, capacity
 * and breaker sizes stay one-click and never replace a value already present.
 */
export const SWITCHBOARD_PHOTO_PROMPT = 'switchboard';
/** Nameplate-adjacent battery shots, in addition to the two strict label prompts. */
export const BATTERY_USABLE_PHOTO_PROMPTS = [
    'battery-system-battery-sign-label',
    'battery-system-battery-system-installation-location-at-distance-close-up',
];
const INVERTER_LABEL_PROMPTS = STRICT_PROMPTS.inverters;
/** Readings the operator must tick. They are never pre-selected. */
export const ACCEPT_ONLY_HARDWARE_FIELDS = new Set([
    'serial',
    'capacityKwh',
    'inverterSerial',
    'circuitBreakerSizeMainSwitchGrid',
    'circuitBreakerSizeMainSwitchInverter',
]);
export const SWITCHBOARD_LABEL_GUIDE = {
    label: 'Switchboard',
    fields: {
        circuitBreakerSizeMainSwitchGrid: 'Current rating in amperes of the breaker whose printed label is MAIN SWITCH (GRID). Quote GRID in printedText with that amp. Plain number such as 63 or 63.5. Never use the INVERTER or ALTERNATIVE breaker.',
        circuitBreakerSizeMainSwitchInverter: 'Current rating in amperes of the breaker whose printed label is MAIN SWITCH (INVERTER). If that label is absent, use MAIN SWITCH (ALTERNATIVE). Quote INVERTER or ALTERNATIVE in printedText with that amp. Plain number. Never use the GRID breaker, even when both labels appear on the same board.',
    },
};
const ROW_PROMPT = /^(inverter|battery)-row:(\d+):(.+)$/;
export function parseHardwarePrompt(promptKey) {
    const match = ROW_PROMPT.exec(promptKey);
    if (!match)
        return { key: promptKey };
    return {
        key: match[3],
        rowKind: match[1] === 'inverter' ? 'inverters' : 'batteries',
        rowIndex: Number(match[2]),
    };
}
export function isSwitchboardPrompt(promptKey) {
    return promptKey === SWITCHBOARD_PHOTO_PROMPT;
}
/**
 * Higher is a better automatic link. Zero means the photo is not offered for
 * this row. Unscoped label prompts stay available on every row of that kind
 * so the operator can re-pick. A row-scoped key only matches its index.
 * Inverter nameplates are offered for a battery only when no inverter row owns
 * them — the usual dashboard mis-file when the job has a battery and no inverter.
 */
export function hardwarePhotoRank(promptKey, kind, index, hardware) {
    if (!promptKey)
        return 0;
    const parsed = parseHardwarePrompt(promptKey);
    if (parsed.rowKind === 'batteries' && kind !== 'batteries')
        return 0;
    if (parsed.rowKind === 'inverters' && kind !== 'inverters' && kind !== 'batteries')
        return 0;
    if (parsed.rowKind === kind && parsed.rowIndex !== index)
        return 0;
    const strict = STRICT_PROMPTS[kind].includes(parsed.key);
    // An inverter nameplate is not a battery label, even though the prompt is strict for inverters.
    if (strict && !(kind === 'batteries' && parsed.rowKind === 'inverters')) {
        const base = parsed.rowKind === kind ? 110 : parsed.rowKind ? 0 : 100;
        // Close-up serial stickers outrank wide model-detail shots of the same unit.
        return /serial/i.test(parsed.key) ? base + 20 : base;
    }
    if (kind === 'batteries' &&
        BATTERY_USABLE_PHOTO_PROMPTS.includes(parsed.key) &&
        (parsed.rowKind === undefined || parsed.rowKind === 'batteries')) {
        return parsed.rowKind === 'batteries' ? 60 : 50;
    }
    if (kind !== 'batteries')
        return 0;
    if (!INVERTER_LABEL_PROMPTS.includes(parsed.key))
        return 0;
    const inverterCount = hardware?.inverters.length ?? 0;
    if (parsed.rowKind === 'inverters') {
        if (parsed.rowIndex !== undefined && parsed.rowIndex < inverterCount)
            return 0;
        if (parsed.rowIndex === undefined && inverterCount > 0)
            return 0;
        return 35;
    }
    if (inverterCount > 0)
        return 0;
    return 30;
}
export function batterySectionPhotoKeys(index) {
    const labels = STRICT_PROMPTS.batteries;
    const extras = BATTERY_USABLE_PHOTO_PROMPTS;
    const keys = [...labels, ...extras];
    return [...keys.map((key) => `battery-row:${index}:${key}`), ...(index === 0 ? keys : [])];
}
export function batteryLabelUploadKey(index) {
    const label = STRICT_PROMPTS.batteries[0];
    return index === 0 ? label : `battery-row:${index}:${label}`;
}
export function inverterSectionPhotoKeys(index) {
    const labels = STRICT_PROMPTS.inverters;
    return [
        ...labels.map((key) => `inverter-row:${index}:${key}`),
        ...(index === 0 ? [...labels] : []),
    ];
}
export function hardwarePhotoEmptyState(kind) {
    const label = KIND_LABEL[kind].toLowerCase();
    return `No ${label} photo to read yet. Upload a label photo in the ${label} section below. Readings appear here after the photo is checked, and nothing is filled in until you accept it.`;
}
export function isBlankHardwareLeaf(value) {
    return (value == null ||
        (typeof value === 'string' && !value.trim()) ||
        (Array.isArray(value) && value.length === 0));
}
export function hardwareBrandKey(value) {
    return (value ?? '').toLowerCase().replace(/[^a-z0-9]+/g, '');
}
/** All-in-one (integrated) or the same brand on the battery and its inverter. */
export function sameVendorOrAllInOne(battery, inverter) {
    if (!battery)
        return false;
    if (battery.systemType === 'integrated')
        return true;
    const left = hardwareBrandKey(battery.make);
    const right = hardwareBrandKey(inverter?.make);
    return left.length > 0 && left === right;
}
/** Connected-inverter serial suggestion. Blank target only; never overwrites. */
export function connectedInverterSerialSuggestion(hardware, batteryIndex = 0) {
    const battery = hardware.batteries[batteryIndex];
    if (!battery || !isBlankHardwareLeaf(battery.inverterSerial))
        return undefined;
    const inverter = hardware.inverters.find((row) => !isBlankHardwareLeaf(row.serial));
    if (!inverter?.serial)
        return undefined;
    if (!sameVendorOrAllInOne(battery, inverter))
        return undefined;
    return inverter.serial.trim();
}
/** Aggregate the field checklist's switchboard and labelling answers. Does not set the checkbox. */
export function switchboardLabellingSuggestion(checklist) {
    const rows = checklist.filter((clause) => /sb\s*&\s*labell/i.test(clause.clauseId));
    if (!rows.length)
        return undefined;
    if (rows.some((row) => row.outcome === 'Non-Compliant'))
        return 'non-compliant';
    if (rows.every((row) => row.outcome === 'Not Applicable'))
        return 'absent';
    if (rows.some((row) => row.outcome === 'Compliant'))
        return 'compliant';
    return 'absent';
}
export function labellingSuggestionCopy(suggestion) {
    if (suggestion === 'compliant')
        return 'Checklist suggests labelling is compliant. This stays unset until you confirm it.';
    if (suggestion === 'non-compliant')
        return 'Checklist suggests labelling is not compliant. Confirm before approving for the portal.';
    return 'Checklist labelling answers are absent or not applicable. Confirm before approving for the portal.';
}
export function withAlternativeSupplyDefault(jobId, hardware) {
    if (!jobId.startsWith('BTY') || hardware.alternativeSupplyFromBessInverter !== undefined)
        return hardware;
    return { ...hardware, alternativeSupplyFromBessInverter: true };
}
/**
 * BTY jobs whose feed has no battery still get Battery 1, so label photos are
 * checked. Make and model stay blank. An existing row is not duplicated.
 * Other jobs are returned unchanged.
 */
export function seedBtyBatteryRow(jobId, hardware) {
    if (!jobId.startsWith('BTY') || hardware.batteries.length > 0)
        return hardware;
    return { ...hardware, batteries: [{}] };
}
/** Writes one battery field. An empty BTY list becomes that single Battery 1 row. */
export function withBatteryRowEdit(jobId, hardware, index, update) {
    const visible = jobId.startsWith('BTY')
        ? seedBtyBatteryRow(jobId, hardware).batteries
        : hardware.batteries.length
            ? hardware.batteries
            : [{}];
    if (!Number.isInteger(index) || index < 0 || index >= visible.length)
        return hardware;
    return {
        ...hardware,
        batteries: visible.map((row, rowIndex) => rowIndex === index ? { ...row, ...update } : { ...row }),
    };
}
/** BTY portal approval needs an explicit labelling answer. Undefined is not a no. */
export function labellingConfirmationRequired(jobId, hardware) {
    return jobId.startsWith('BTY') && hardware?.switchboardLabellingRequirementsMet === undefined;
}
export function batteryInstallationLabel(value) {
    if (value === 'concurrent')
        return 'Concurrent with PV';
    if (value === 'subsequent')
        return 'Retrofitted to existing PV';
    return undefined;
}
/**
 * Field-app battery location tap.
 *
 * The environment flag turns the tap on for every inspector. The id list
 * turns it on for named inspectors while the flag stays off, so production
 * can trial one person. Unset flag and an empty list leave the tap hidden.
 */
export function batteryLocationCaptureEnabled(input) {
    if (input.enabled)
        return true;
    const inspectorId = input.inspectorId?.trim();
    if (!inspectorId)
        return false;
    return input.inspectorIds.some((id) => id.trim() === inspectorId);
}
export function batteryLocationTapVisible(input) {
    return input.enabled && input.inspectionType === 'battery';
}
/**
 * Warn before accepting a serial that was filed under the other equipment kind,
 * or whose printed text names the other kind. Accept stays manual.
 */
export function serialKindMismatchWarning(kind, reading, promptKey) {
    if (reading.field !== 'serial' && reading.field !== 'inverterSerial')
        return undefined;
    const text = `${reading.printedText ?? ''} ${reading.value ?? ''}`.toLowerCase();
    const parsed = promptKey ? parseHardwarePrompt(promptKey) : undefined;
    const promptKeyBase = parsed?.key ?? '';
    const textSaysInverter = /\binverter\b/.test(text);
    const textSaysBattery = /\bbattery\b/.test(text);
    const filedAsBattery = parsed?.rowKind === 'batteries' ||
        STRICT_PROMPTS.batteries.includes(promptKeyBase) ||
        BATTERY_USABLE_PHOTO_PROMPTS.includes(promptKeyBase);
    const filedAsInverter = parsed?.rowKind === 'inverters' || STRICT_PROMPTS.inverters.includes(promptKeyBase);
    if (kind === 'batteries' && (textSaysInverter || filedAsInverter) && !textSaysBattery) {
        return 'This serial reading looks like an inverter label. Confirm before accepting, or check the inverter row instead.';
    }
    if (kind === 'inverters' && (textSaysBattery || filedAsBattery) && !textSaysInverter) {
        return 'This serial came from a battery photo (or looks like a battery label). Confirm before accepting, or check the battery row instead.';
    }
    return undefined;
}
//# sourceMappingURL=bty-hardware.js.map